/**
 * Files Teslyn Field uploads to Teslyn Cloud (docs/specs/cloud/storage-uploads.md): the bucket and object name of
 * each kind, its content types, and the checks made before an upload. The buckets enforce the same limits on the
 * server (supabase/migrations/20261020000000_storage_uploads.sql).
 *
 * - `report_pdf`: a daily or weekly report's PDF, at its report's path in `reports` (the path the portal's "Attach
 *   PDF" uses), so the portal shows it without staff attaching it (decision 185).
 * - `fuel_docket`: a resupply's delivery docket, a photo or a PDF (decisions 159, 193), named by the entry and the
 *   file's SHA-256 so a replacement is a new object and never overwrites the old one.
 * - `photo`, `photo_display`, `photo_thumb`: a project photo as it was added, and its display size and thumbnail
 *   (JPEG), named by the photo (decisions 427-431, docs/specs/cloud/project-photos.md).
 * - `document`: a picture of an MR page, a fuel docket or a receipt (F03, F09; docs/specs/document-reading.md §3),
 *   named by its record; a picture never changes, so its object is written once.
 * - `breach_chart`: the chart of a Height breach report entry (R197, decision 1017), in the private bucket
 *   `height-breach`, named by the entry and its SHA-256; staff of the project read it, clients never.
 * - `breach_report`: the Height breach report's PDF (decision 1034, R219), in the private bucket `breach-report`, named
 *   by the project and its SHA-256; one record per project, the latest sent. Staff of the project read it; a client
 *   while at least one of the project's breaches is shown to them (20261104237000_breach_report_pdf.sql).
 */
import { reportStoragePath } from './portal.js';
import { photoObjectPath, PHOTO_BUCKET, PHOTO_MAX_BYTES, PHOTO_TYPES } from './photos.js';
import { DOCUMENT_FILE_TYPES, DOCUMENT_MAX_BYTES } from './document-reading.js';

export type UploadKind =
  | 'report_pdf'
  | 'fuel_docket'
  | 'photo'
  | 'photo_display'
  | 'photo_thumb'
  | 'document'
  | 'brief_pdf'
  | 'breach_chart'
  | 'breach_report';

/** The bucket of the Height breach report's charts (decision 1017; 20261104000000_height_breach.sql). */
export const BREACH_CHART_BUCKET = 'height-breach';

/** The bucket of the Height breach report's PDF (decision 1034; 20261104237000_breach_report_pdf.sql). */
export const BREACH_REPORT_BUCKET = 'breach-report';

/** The bucket of the document pictures (docs/specs/document-reading.md §3). */
export const DOCUMENT_BUCKET = 'documents';

/** The upload kinds of a project photo's three files. */
export const PHOTO_UPLOAD_KINDS = ['photo', 'photo_display', 'photo_thumb'] as const;

export interface UploadKindRule {
  readonly bucket: string;
  readonly maxBytes: number;
  /** Allowed lower-case extensions and the content type each is sent with. */
  readonly types: Readonly<Record<string, string>>;
  /** What the refusal says when the type is wrong. */
  readonly typeMessage: string;
}

const MB = 1024 * 1024;

export const UPLOAD_KINDS: Readonly<Record<UploadKind, UploadKindRule>> = {
  report_pdf: {
    bucket: 'reports',
    maxBytes: 50 * MB,
    types: { pdf: 'application/pdf' },
    typeMessage: 'A report must be a PDF.',
  },
  fuel_docket: {
    bucket: 'fuel-dockets',
    // Field's limit when the docket is chosen (fuel_docket_attach, DOCKET_MAX_BYTES)
    maxBytes: 30 * MB,
    types: {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      heic: 'image/heic',
      webp: 'image/webp',
    },
    typeMessage: 'A delivery docket must be a photo (JPG, PNG, HEIC, WebP) or a PDF.',
  },
  photo: {
    bucket: PHOTO_BUCKET,
    maxBytes: PHOTO_MAX_BYTES,
    types: PHOTO_TYPES,
    typeMessage: 'A project photo must be a JPEG, PNG or WebP image.',
  },
  photo_display: {
    bucket: PHOTO_BUCKET,
    maxBytes: PHOTO_MAX_BYTES,
    types: { jpg: 'image/jpeg' },
    typeMessage: 'The display size of a photo is a JPEG.',
  },
  photo_thumb: {
    bucket: PHOTO_BUCKET,
    maxBytes: PHOTO_MAX_BYTES,
    types: { jpg: 'image/jpeg' },
    typeMessage: 'The thumbnail of a photo is a JPEG.',
  },
  document: {
    bucket: DOCUMENT_BUCKET,
    maxBytes: DOCUMENT_MAX_BYTES,
    types: DOCUMENT_FILE_TYPES,
    typeMessage: 'A document must be a photo (JPG, PNG or WebP) or a PDF scan.',
  },
  // a pre-flight brief's or safety meeting's PDF, made on a laptop (docs/specs/safety-brief.md §7): bucket safety
  // (20261102190000, 50 MB, PDF), named by its content so a newer PDF is a new object
  brief_pdf: {
    bucket: 'safety',
    maxBytes: 50 * MB,
    types: { pdf: 'application/pdf' },
    typeMessage: 'A brief’s file must be a PDF.',
  },
  // a Height breach report entry's chart, drawn on a laptop (docs/specs/geosoft-qc.md §28): a PNG of 2800 by 780
  // pixels, well under 10 MB; named by its content
  breach_chart: {
    bucket: BREACH_CHART_BUCKET,
    maxBytes: 10 * MB,
    types: { png: 'image/png' },
    typeMessage: 'A breach chart is a PNG.',
  },
  // the Height breach report's PDF, written on a laptop (decision 1034): MB's F26044 report is 0.4 MB with three
  // entries; a long survey's hundreds of entries stay well under 50 MB
  breach_report: {
    bucket: BREACH_REPORT_BUCKET,
    maxBytes: 50 * MB,
    types: { pdf: 'application/pdf' },
    typeMessage: 'A breach report must be a PDF.',
  },
};

/** The last part of a path written on Windows or with forward slashes. */
export function fileBaseName(path: string): string {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] ?? path;
}

/** The lower-case extension of a path, or '' when it has none. */
export function fileExtension(path: string): string {
  const name = fileBaseName(path);
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

export type UploadCheck =
  | { readonly ok: true; readonly ext: string; readonly contentType: string }
  | { readonly ok: false; readonly code: 'bad_type' | 'too_large' | 'empty'; readonly message: string };

/** Whether a file of `sizeBytes` at `path` may be uploaded as `kind`, with its content type. */
export function uploadCheck(kind: UploadKind, path: string, sizeBytes: number): UploadCheck {
  const rule = UPLOAD_KINDS[kind];
  const ext = fileExtension(path);
  const contentType = rule.types[ext];
  if (!contentType) return { ok: false, code: 'bad_type', message: rule.typeMessage };
  if (sizeBytes <= 0) return { ok: false, code: 'empty', message: 'The file is empty.' };
  if (sizeBytes > rule.maxBytes) {
    return {
      ok: false,
      code: 'too_large',
      message: `The file is ${(sizeBytes / MB).toFixed(1)} MB; the limit is ${rule.maxBytes / MB} MB.`,
    };
  }
  return { ok: true, ext, contentType };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const SHA256 = /^[0-9a-f]{64}$/;

function uuid(v: string): string {
  if (!UUID.test(v)) throw new Error(`${v} is not a valid id.`);
  return v;
}

/** `tenants/<tenant>/projects/<project>/fuel-dockets/<entry>/<sha256>.<ext>`. */
export function fuelDocketStoragePath(
  tenantId: string,
  projectId: string,
  entryId: string,
  sha256: string,
  ext: string,
): string {
  if (!SHA256.test(sha256)) throw new Error('The file hash is not a SHA-256 in lower-case hex.');
  if (!(ext in UPLOAD_KINDS.fuel_docket.types)) throw new Error(UPLOAD_KINDS.fuel_docket.typeMessage);
  return `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/fuel-dockets/${uuid(entryId)}/${sha256}.${ext}`;
}

/** `tenants/<tenant>/projects/<project>/documents/<scan>.<ext>` (docs/specs/document-reading.md §3). */
export function documentObjectPath(tenantId: string, projectId: string, scanId: string, ext: string): string {
  const e = ext.toLowerCase();
  if (!(e in DOCUMENT_FILE_TYPES)) throw new Error(UPLOAD_KINDS.document.typeMessage);
  return `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/documents/${uuid(scanId)}.${e}`;
}

/** `tenants/<tenant>/projects/<project>/safety/briefs/<brief>/<sha256>.pdf` (20261103110000 `brief_object_parts`). */
export function briefPdfObjectPath(
  tenantId: string,
  projectId: string,
  briefId: string,
  sha256: string,
): string {
  if (!SHA256.test(sha256)) throw new Error('The file hash is not a SHA-256 in lower-case hex.');
  return `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/safety/briefs/${uuid(briefId)}/${sha256}.pdf`;
}

/** `tenants/<tenant>/projects/<project>/height-breach/<entry>/<sha256>.png`. */
export function breachChartObjectPath(
  tenantId: string,
  projectId: string,
  entryId: string,
  sha256: string,
): string {
  if (!SHA256.test(sha256)) throw new Error('The file hash is not a SHA-256 in lower-case hex.');
  return `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/height-breach/${uuid(entryId)}/${sha256}.png`;
}

/** `tenants/<tenant>/projects/<project>/breach-report/<sha256>.pdf` (20261104237000 `breach_report_object_parts`). */
export function breachReportObjectPath(tenantId: string, projectId: string, sha256: string): string {
  if (!SHA256.test(sha256)) throw new Error('The file hash is not a SHA-256 in lower-case hex.');
  return `tenants/${uuid(tenantId)}/projects/${uuid(projectId)}/breach-report/${sha256}.pdf`;
}

export interface UploadTargetInput {
  readonly tenantId: string;
  readonly projectId: string;
  /** The report, fuel entry or photo. */
  readonly recordId: string;
  readonly ext: string;
  readonly sha256: string;
}

export interface UploadTarget {
  readonly bucket: string;
  readonly objectPath: string;
}

/** Where a file of `kind` goes in Teslyn Cloud. */
export function uploadTarget(kind: UploadKind, t: UploadTargetInput): UploadTarget {
  const bucket = UPLOAD_KINDS[kind].bucket;
  if (kind === 'report_pdf') {
    return { bucket, objectPath: reportStoragePath(uuid(t.tenantId), uuid(t.projectId), uuid(t.recordId)) };
  }
  if (kind === 'photo')
    return { bucket, objectPath: photoObjectPath(t.tenantId, t.projectId, t.recordId, 'original', t.ext) };
  if (kind === 'photo_display')
    return { bucket, objectPath: photoObjectPath(t.tenantId, t.projectId, t.recordId, 'display') };
  if (kind === 'photo_thumb')
    return { bucket, objectPath: photoObjectPath(t.tenantId, t.projectId, t.recordId, 'thumb') };
  if (kind === 'document')
    return { bucket, objectPath: documentObjectPath(t.tenantId, t.projectId, t.recordId, t.ext) };
  if (kind === 'brief_pdf')
    return { bucket, objectPath: briefPdfObjectPath(t.tenantId, t.projectId, t.recordId, t.sha256) };
  if (kind === 'breach_chart')
    return { bucket, objectPath: breachChartObjectPath(t.tenantId, t.projectId, t.recordId, t.sha256) };
  if (kind === 'breach_report') {
    // one record per project: the record is the project itself
    if (t.recordId !== t.projectId) throw new Error('A breach report belongs to its project.');
    return { bucket, objectPath: breachReportObjectPath(t.tenantId, t.projectId, t.sha256) };
  }
  return { bucket, objectPath: fuelDocketStoragePath(t.tenantId, t.projectId, t.recordId, t.sha256, t.ext) };
}
